import { useState } from 'react';
import { useParams } from 'react-router-dom';
import InvoiceHeader from '../components/results/InvoiceHeader';
import AiExecutiveSummary from '../components/results/AiExecutiveSummary';
import InvoiceHeroCard from '../components/results/InvoiceHeroCard';
import DocumentViewer from '../components/results/DocumentViewer';
import VendorCustomerCards from '../components/results/VendorCustomerCards';
import LineItemsTable, { LineItem } from '../components/results/LineItemsTable';
import GroupSharingCard from '../components/results/GroupSharingCard';
import OtherDetailsCard from '../components/results/OtherDetailsCard';
import InvoiceHistoryCard from '../components/results/InvoiceHistoryCard';
import { useInvoiceDetail } from '../api/useInvoiceDetail';
import { useRestoreInvoice } from '../api/useRestoreInvoice';
import { extractString, extractNumber, extractStringArray } from '../utils/sanitizeData';
import { getInvoiceQualitySummary } from '../utils/dataQuality';

export default function InvoiceResultPage() {
  const { id: publicToken } = useParams<{ id?: string }>();
  const [selectedHistoryToken, setSelectedHistoryToken] = useState<string | null>(null);
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);

  // Fetch invoice details (supports previewing past versions via history_token)
  const { data: invoice, isLoading } = useInvoiceDetail(publicToken, selectedHistoryToken || undefined);
  const restoreInvoiceMutation = useRestoreInvoice();

  const qualitySummary = invoice ? getInvoiceQualitySummary(invoice) : undefined;

  const handleRestoreVersion = async (historyToken: string) => {
    if (!publicToken) return;
    if (confirm('Are you sure you want to restore this invoice to this historical version? A new revision entry will be created.')) {
      try {
        await restoreInvoiceMutation.mutateAsync({
          publicToken,
          historyToken,
        });
        setSelectedHistoryToken(null);
        setRestoreNotice('Invoice successfully restored to selected historical revision.');
        setTimeout(() => setRestoreNotice(null), 5000);
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : 'Failed to restore invoice version.');
      }
    }
  };

  const additionalFields = (invoice?.fields?.additional_fields ?? {}) as Record<string, unknown>;
  const parsedInvoiceId = extractString(
    invoice?.fields?.invoice_meta?.invoice_id ||
    invoice?.fields?.invoice_id ||
    additionalFields['InvoiceId'] ||
    additionalFields['invoice_id']
  );

  const invoiceNumber = parsedInvoiceId || '';

  const vendorName = extractString(invoice?.vendor_name || invoice?.fields?.vendor?.name, 'Unknown Vendor');
  const invoiceDate = extractString(invoice?.invoice_date || invoice?.fields?.invoice_meta?.invoice_date, '—');
  const dueDate = extractString(invoice?.due_date || invoice?.fields?.invoice_meta?.due_date, '—');
  const totalAmount = extractString(
    invoice?.invoice_total?.amount || invoice?.fields?.totals?.invoice_total,
    '0.00'
  );
  const currency = extractString(
    invoice?.invoice_total?.currency || invoice?.fields?.totals?.currency,
    'EUR'
  );
  const tags = extractStringArray(invoice?.tags);

  // Map API fields safely into customer & vendor cards with clean fallbacks
  const vendorDetails = {
    name: vendorName,
    address: extractString(invoice?.fields?.vendor?.address || invoice?.fields?.vendor_address, '—'),
    vatNumber: extractString(invoice?.fields?.vendor?.tax_id || invoice?.fields?.vendor_vat, '—'),
    email: extractString(invoice?.fields?.vendor?.email || invoice?.fields?.vendor_email, '—'),
    iban: extractString(invoice?.fields?.vendor?.iban || invoice?.fields?.vendor_iban, '—'),
  };

  const customerDetails = {
    name: extractString(invoice?.fields?.customer?.name || invoice?.fields?.customer_name, '—'),
    address: extractString(invoice?.fields?.customer?.address || invoice?.fields?.customer_address, '—'),
    vatNumber: extractString(invoice?.fields?.customer?.tax_id || invoice?.fields?.customer_vat, '—'),
    email: extractString(invoice?.fields?.customer?.email || invoice?.fields?.customer_email, '—'),
    accountNumber: extractString(
      invoice?.fields?.customer?.id ||
        invoice?.fields?.invoice_meta?.purchase_order ||
        invoice?.fields?.po_number,
      '—'
    ),
  };

  // Map API line items safely (empty array if none provided)
  const lineItems: LineItem[] =
    invoice?.items && Array.isArray(invoice.items) && invoice.items.length > 0
      ? invoice.items.map((item, idx) => ({
          id: extractString(item.id, String(idx + 1)),
          description: extractString(item.description, 'Line Item'),
          quantity: extractNumber(item.quantity, 1),
          unitPrice: extractNumber(item.unit_price ?? item.amount, 0),
          amount: extractNumber(item.amount, 0),
          taxRate: item.tax_rate ? extractString(item.tax_rate) : undefined,
        }))
      : [];

  const subtotal =
    invoice?.fields?.totals?.subtotal !== undefined && invoice?.fields?.totals?.subtotal !== null
      ? extractNumber(invoice.fields.totals.subtotal)
      : invoice?.fields?.subtotal !== undefined && invoice.fields.subtotal !== null
      ? extractNumber(invoice.fields.subtotal)
      : undefined;

  const taxAmount =
    invoice?.fields?.totals?.total_tax !== undefined && invoice?.fields?.totals?.total_tax !== null
      ? extractNumber(invoice.fields.totals.total_tax)
      : invoice?.fields?.tax_amount !== undefined && invoice.fields.tax_amount !== null
      ? extractNumber(invoice.fields.tax_amount)
      : undefined;

  const taxRate = invoice?.fields?.totals?.tax_rate
    ? extractString(invoice.fields.totals.tax_rate)
    : invoice?.fields?.tax_rate
    ? extractString(invoice.fields.tax_rate)
    : undefined;

  const sharedGroups = invoice?.sharing?.shared_with || [];
  const aiSummaryText = invoice?.ai_summary ? extractString(invoice.ai_summary) : undefined;

  return (
    <div className="max-w-[1360px] mx-auto py-2 sm:py-4">
      {/* Header navigation and status badges */}
      <InvoiceHeader
        invoiceNumber={invoiceNumber}
        publicToken={publicToken}
        qualitySummary={qualitySummary}
        reconciliationStatus={invoice?.status?.is_success ? 'matched' : 'pending'}
      />

      {/* Historical Revision Banner (When viewing past version) */}
      {selectedHistoryToken && (
        <div className="mb-6 p-4 rounded-2.5xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-amber-800 dark:text-amber-300 shadow-sm">
          <div className="flex items-center gap-2.5">
            <span className="text-xl">🕒</span>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider font-mono">
                Viewing Historical Revision
              </div>
              <div className="text-xs opacity-90 mt-0.5">
                You are previewing a past scan/edit version of this invoice in read-only mode.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              type="button"
              onClick={() => handleRestoreVersion(selectedHistoryToken)}
              disabled={restoreInvoiceMutation.isPending}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-vib hover:bg-brand-vib-hover text-white transition-colors cursor-pointer shadow-xs disabled:opacity-50"
            >
              {restoreInvoiceMutation.isPending ? 'Restoring…' : 'Restore This Version'}
            </button>
            <button
              type="button"
              onClick={() => setSelectedHistoryToken(null)}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white transition-colors cursor-pointer shadow-xs"
            >
              Return to Current
            </button>
          </div>
        </div>
      )}

      {/* Restore Success Toast Notification */}
      {restoreNotice && (
        <div className="mb-6 p-4 rounded-2.5xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 flex items-center justify-between gap-3 text-emerald-800 dark:text-emerald-300 shadow-sm animate-fadeIn">
          <div className="flex items-center gap-2.5">
            <span>✅</span>
            <span className="text-xs font-semibold font-mono">{restoreNotice}</span>
          </div>
          <button
            type="button"
            onClick={() => setRestoreNotice(null)}
            className="text-xs font-bold opacity-60 hover:opacity-100 cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}

      {/* AI Executive Summary */}
      <AiExecutiveSummary
        publicToken={publicToken}
        summary={aiSummaryText}
        isLoading={isLoading}
      />

      {/* Main Full-Width Content Stack */}
      <div className="flex flex-col gap-0">
        <InvoiceHeroCard
          publicToken={publicToken}
          vendorName={vendorName}
          invoiceDate={invoiceDate}
          dueDate={dueDate}
          totalAmount={totalAmount}
          currency={currency}
          initialTags={tags}
        />

        <DocumentViewer
          documentName={`${vendorName !== 'Unknown Vendor' ? vendorName : 'Invoice'}-${invoiceDate !== '—' ? invoiceDate : 'Document'}.pdf`}
          documentUrl={
            extractString((invoice as unknown as Record<string, unknown>)?.document_url) ||
            extractString(invoice?.actions?.view_url) ||
            extractString(invoice?.actions?.download_url) ||
            (publicToken ? `/api/v2/invoices/${publicToken}/download/` : '#')
          }
        />

        <VendorCustomerCards
          vendor={vendorDetails}
          customer={customerDetails}
        />

        {/* Group Sharing Card */}
        <div className="mb-6">
          <GroupSharingCard
            publicToken={publicToken}
            initialGroups={sharedGroups}
          />
        </div>

        {/* Line Items Breakdown */}
        <LineItemsTable
          items={lineItems}
          subtotal={subtotal}
          taxAmount={taxAmount}
          taxRate={taxRate}
          totalAmount={extractNumber(totalAmount, 0)}
          currency={currency}
        />

        {/* Other Details & Financial Metadata Card */}
        <OtherDetailsCard
          fields={invoice?.fields}
          currency={currency}
          defaultOpen={true}
        />

        {/* Invoice Revision History Component */}
        <InvoiceHistoryCard
          histories={invoice?.histories}
          selectedHistoryId={selectedHistoryToken}
          onSelectVersion={setSelectedHistoryToken}
          onRestoreVersion={handleRestoreVersion}
          isRestoring={restoreInvoiceMutation.isPending}
        />
      </div>
    </div>
  );
}
